import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import type { ComponentType } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BottomNav } from "@/components/sartn/BottomNav";
import { ErrorBoundary } from "@/components/sartn/ErrorBoundary";
import { OfflineIndicator } from "@/components/sartn/OfflineIndicator";
import { OnboardingTutorial } from "@/components/sartn/OnboardingTutorial";
import { useAppStore } from "@/stores/appStore";
import { useAuthStore } from "@/stores/authStore";
import { registerServiceWorker } from "@/lib/serviceWorker";
import {
  getTelegramUser,
  getInitData,
  getColorScheme,
  setHeaderColor,
  setBackgroundColor,
  expandApp,
  markAppReady,
  enableClosingConfirmation,
} from "@/lib/telegram";
import HomePage from "@/pages/Home";
import ChatsPage from "@/pages/Chats";
import AutomationsPage from "@/pages/Automations";
import SettingsPage from "@/pages/Settings";
import ContactsPage from "@/pages/Contacts";
import ContactDetail from "@/pages/Contacts/ContactDetail";
import ContactAdd from "@/pages/Contacts/ContactAdd";
import GroupsPage from "@/pages/Groups";
import { GroupSettingsPage } from "@/pages/Groups/GroupSettings";
import ChannelsPage from "@/pages/Channels";
import { ChannelDetailPage } from "@/pages/Channels/ChannelDetail";
import { VoiceSettings } from "@/pages/Settings/VoiceSettings";
import BusinessPage from "@/pages/Business";
import ProductForm from "@/pages/Business/ProductForm";
import OrderDetail from "@/pages/Business/OrderDetail";
import SubscriptionsPage from "@/pages/Subscriptions";
import { PlanForm } from "@/pages/Subscriptions/PlanForm";
import { AutomationWizard } from "@/pages/Automations/AutomationWizard";
import NotFoundPage from "@/pages/NotFound";
import GrowthPage from "@/pages/Growth";

// Lazy-loaded pages — reduces initial bundle size
const IntelligencePage = lazy(() => import("@/pages/Intelligence/index"));
const AddCompetitor = lazy(() => import("@/pages/Intelligence/AddCompetitor"));
const MarketingPage = lazy(() => import("@/pages/Marketing/index"));
const CampaignForm = lazy(() =>
  import("@/pages/Marketing/CampaignForm").then((mod) => ({ default: mod.CampaignForm })),
);
const TeamPage = lazy(() => import("@/pages/Team/index"));
const MarketPage = lazy(() => import("@/pages/Market/index"));
const LandingPage = lazy(() => import("@/pages/Landing/index"));
const PrivacyPage = lazy(() => import("@/pages/Legal/Privacy"));
const TermsPage = lazy(() => import("@/pages/Legal/Terms"));

const PAGE_TRANSITION = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.2, ease: "easeInOut" as const },
};

function getHashRoute(): string {
  const hash = window.location.hash.replace("#", "");
  return hash || "/";
}

function extractContactId(route: string): number | null {
  const match = route.match(/^\/contacts\/(\d+)$/);
  return match ? Number(match[1]) : null;
}

function extractGroupId(route: string): string | null {
  const match = route.match(/^\/groups\/(.+)$/);
  return match?.[1] ?? null;
}

function extractChannelId(route: string): string | null {
  const match = route.match(/^\/channels\/(.+)$/);
  return match?.[1] ?? null;
}

function extractOrderId(route: string): string | null {
  const match = route.match(/^\/business\/orders\/(.+)$/);
  return match?.[1] ?? null;
}

type ResolvedPage =
  | ComponentType
  | { component: React.FC<{ contactId: number }>; contactId: number }
  | { component: React.FC<{ groupId: string }>; groupId: string }
  | { component: React.FC<{ channelId: string }>; channelId: string }
  | { component: React.FC<{ orderId: string }>; orderId: string };

function resolvePageComponent(route: string): ResolvedPage {
  if (route === "/contacts/add") return ContactAdd;
  if (route === "/settings/voice") return VoiceSettings;
  if (route === "/business/add-product") return ProductForm;
  if (route === "/subscriptions/new-plan") return PlanForm;
  if (route === "/automations/new") return AutomationWizard;
  if (route === "/intelligence") return IntelligencePage;
  if (route === "/intelligence/add") return AddCompetitor;
  if (route === "/marketing") return MarketingPage;
  if (route === "/marketing/new-campaign") return CampaignForm;
  if (route === "/team") return TeamPage;
  if (route === "/market") return MarketPage;
  if (route === "/landing") return LandingPage;
  if (route === "/privacy") return PrivacyPage;
  if (route === "/terms") return TermsPage;

  const contactId = extractContactId(route);
  if (contactId !== null) return { component: ContactDetail, contactId };

  const groupId = extractGroupId(route);
  if (groupId !== null) return { component: GroupSettingsPage, groupId };

  const channelId = extractChannelId(route);
  if (channelId !== null) return { component: ChannelDetailPage, channelId };

  const orderId = extractOrderId(route);
  if (orderId !== null) return { component: OrderDetail, orderId };

  switch (route) {
    case "/":
      return HomePage;
    case "/contacts":
      return ContactsPage;
    case "/channels":
      return ChannelsPage;
    case "/groups":
      return GroupsPage;
    case "/chats":
      return ChatsPage;
    case "/growth":
      return GrowthPage;
    case "/automations":
      return AutomationsPage;
    case "/settings":
      return SettingsPage;
    case "/business":
      return BusinessPage;
    case "/subscriptions":
      return SubscriptionsPage;
    default:
      return NotFoundPage;
  }
}

export function App() {
  const [currentRoute, setCurrentRoute] = useState(getHashRoute);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem("sartn_onboarding_complete")
  );
  const setActiveTab = useAppStore((state) => state.setActiveTab);
  const setDarkMode = useAppStore((state) => state.setDarkMode);
  const initializeSession = useAuthStore((state) => state.initializeSession);
  const isInitializingAuth = useAuthStore((state) => state.isInitializing);
  const authError = useAuthStore((state) => state.error);
  const telegramUser = useAuthStore((state) => state.user);

  const navigateTo = useCallback(
    (path: string) => {
      window.location.hash = path;
      setCurrentRoute(path);
      setActiveTab(path);
    },
    [setActiveTab],
  );

  useEffect(() => {
    function handleHashChange() {
      const route = getHashRoute();
      setCurrentRoute(route);
      setActiveTab(route);
    }

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [setActiveTab]);

  useEffect(() => {
    const currentTelegramUser = getTelegramUser();
    void initializeSession(currentTelegramUser, getInitData()).finally(markAppReady);

    const colorScheme = getColorScheme();
    setDarkMode(colorScheme === "dark");

    setHeaderColor("#f8fafc");
    setBackgroundColor("#f8fafc");
    expandApp();
    enableClosingConfirmation();
    registerServiceWorker();
  }, [initializeSession, setDarkMode]);

  useEffect(() => {
    setActiveTab(currentRoute);
  }, [currentRoute, setActiveTab]);

  const isStandalonePage =
    currentRoute === "/landing" ||
    currentRoute === "/privacy" ||
    currentRoute === "/terms";

  const resolved = resolvePageComponent(currentRoute);

  function renderPage() {
    if ("component" in resolved) {
      if ("contactId" in resolved) {
        const { component: DetailComponent, contactId } = resolved;
        return <DetailComponent contactId={contactId} />;
      }
      if ("channelId" in resolved) {
        const { component: ChannelComponent, channelId } = resolved;
        return <ChannelComponent channelId={channelId} />;
      }
      if ("orderId" in resolved) {
        const { component: OrderComponent, orderId } = resolved;
        return <OrderComponent orderId={orderId} />;
      }
      const { component: GroupComponent, groupId } = resolved;
      return <GroupComponent groupId={groupId} />;
    }

    const PageComponent = resolved;
    return <PageComponent />;
  }

  if (isInitializingAuth) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-bg)] text-sm text-[var(--color-text-muted)]">
        Connecting securely…
      </div>
    );
  }

  if (authError && telegramUser) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-[var(--color-bg)] px-6 text-center">
        <p className="font-semibold text-[var(--color-text)]">Could not connect to SARTN</p>
        <p className="text-sm text-[var(--color-text-muted)]">{authError}</p>
        <button
          type="button"
          onClick={() => void initializeSession(getTelegramUser(), getInitData())}
          className="rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white"
        >
          Try again
        </button>
      </div>
    );
  }

  if (showOnboarding) {
    return <OnboardingTutorial onComplete={() => setShowOnboarding(false)} />;
  }

  return (
    <ErrorBoundary>
      <OfflineIndicator />
      <div className="flex min-h-dvh flex-col bg-[var(--color-bg)]">
        <Suspense fallback={<div className="flex flex-1 items-center justify-center" />}>
          <AnimatePresence mode="wait">
            <motion.div
              key={currentRoute}
              initial={PAGE_TRANSITION.initial}
              animate={PAGE_TRANSITION.animate}
              exit={PAGE_TRANSITION.exit}
              transition={PAGE_TRANSITION.transition}
              className="flex flex-1 flex-col"
            >
              {renderPage()}
            </motion.div>
          </AnimatePresence>
        </Suspense>

        {!isStandalonePage && <BottomNav onNavigate={navigateTo} />}
      </div>
    </ErrorBoundary>
  );
}
